import React from 'react';
import PropTypes from 'prop-types';
import LazyImage, {
  lazyLoadPropType,
  lazyLoadDefaultProp,
} from '../lazy-image';
import PlaceholderImage from '../placeholder-image';

/**
 * Render an image media type from Contentful.
 *
 * If passed `lazy` will render it as lazy loaded.
 * See ../lazy-image for more info
 *
 * It utilizes the Contentful Image API to retrieve a low quality
 * placeholder image.
 * This prevents content from jumping around when users scroll and
 * images have not yet loaded.
 * The low quality image also is Server Side Rendered.
 */
// eslint-disable-next-line react/prefer-stateless-function
class Image extends React.PureComponent {
  render() {
    const {
      className,
      height,
      lazy,
      lazyLoadProps,
      media,
      width,
      imageProps,
      quality,
    } = this.props;

    const queryPropsMap = {
      ...(width ? { w: width } : {}),
      ...(height ? { h: height } : {}),
      // If a quality is provided, convert to a progressive jpg
      ...(quality ? { q: quality, fm: 'jpg', fl: 'progressive' } : {}),
    };

    const queryProps = String(new URLSearchParams(queryPropsMap));

    const src =
      typeof media === 'string' ? media : media?.fields?.file?.url ?? '';
    const alt = media?.fields?.file?.title ?? '';

    // format is jpg for all browser support and quality manipulation
    // We put q=1 after queryProps so it will override whatever is
    // provided by quality.
    // const lowQualitySrc = `${src}?fm=jpg&${queryProps}&q=1`;
    const highQualitySrc = `${src}?${queryProps}`;
    const placeholder = (
      <PlaceholderImage
        className={className}
        alt={alt}
        width={width}
        height={height}
      />
    );

    return (
      <LazyImage
        alt={alt}
        className={className}
        lazy={lazy}
        lazyLoadProps={lazyLoadProps}
        placeholder={placeholder}
        src={highQualitySrc}
        imageProps={imageProps}
      />
    );
  }
}


export const imagePropTypes = {
  className: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lazy: PropTypes.bool,
  media: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      fields: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
          title: PropTypes.string,
        }),
      }),
      sys: PropTypes.shape({}),
    }),
  ]),
  quality: PropTypes.number,
  // see ../LazyImage for prop options
  lazyLoadProps: PropTypes.shape({ ...lazyLoadPropType }),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  imageProps: PropTypes.shape({}),
};

export const imageDefaultProps = {
  className: '',
  height: null,
  lazy: true,
  media: {},
  quality: null,
  // lazy load props
  lazyLoadProps: lazyLoadDefaultProp,
  width: null,
  imageProps: {},
};

Image.propTypes = imagePropTypes;
Image.defaultProps = imageDefaultProps;

export default Image;
